import type { AmsFundingProgram, PublicFundingProgramStatus } from "@/app/appointments/booking-season/bookingSeasonApi"

/**
 * What the household step says about switched-off funding programs (AB#607), from the public season
 * payload's `programs`. Each is the administrator's own message, or null when there is nothing to say.
 * The options themselves stay selectable, as the energy source is a fact about the household (AB#595);
 * the guidelines step withholds a switched-off program, and the server refuses a booking for which
 * every program is off.
 */
export interface FundingSwitchNotices {
  /** Beside Puget Sound Energy: both PSE HELP programs are off. */
  pse: string | null
  /** Below the energy source: LIHEAP is off. */
  liheap: string | null
  /** Beside Gas: PSE HELP gas is off while electric is on. */
  gas: string | null
  /** Beside Electric: PSE HELP electric is off while gas is on. */
  electric: string | null
}

export function fundingSwitchNotices(programs: readonly PublicFundingProgramStatus[] | undefined): FundingSwitchNotices {
  const find = (program: AmsFundingProgram) => programs?.find((p) => p.program === program)
  const off = (program: AmsFundingProgram) => find(program)?.isEnabled === false
  const message = (program: AmsFundingProgram) => find(program)?.message ?? null

  const gasOff = off("PseHelpGas")
  const electricOff = off("PseHelpElectric")

  return {
    pse: gasOff && electricOff ? distinct([message("PseHelpGas"), message("PseHelpElectric")]) : null,
    liheap: off("Liheap") ? message("Liheap") : null,
    gas: gasOff && !electricOff ? message("PseHelpGas") : null,
    electric: electricOff && !gasOff ? message("PseHelpElectric") : null,
  }
}

function distinct(messages: (string | null)[]): string | null {
  const unique = [...new Set(messages.filter((m): m is string => !!m))]
  return unique.length ? unique.join(" ") : null
}
